import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Skeleton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useIsMobile } from '../hooks/useIsMobile';
import { ButtonBasic } from '../components/button/ButtonBasic.jsx';
import CafeAdminModal from '../template/magazine/CafeAdminModal';
import CafeAdminListModal from '../template/magazine/CafeAdminListModal';
import CafeCard from '../template/magazine/CafeCard';
import CafeDetailModal from '../template/magazine/CafeDetailModal';
import { subscribeCafeList, deleteCafe } from '../services/cafeService';
import { useUser } from '../contexts/UserContext';
import { checkAdminPermission } from '../services/adminAuth';
import { PageContainer } from '../components/Container/PageContainer';
import { dbService } from '../firebaseInstance';

/**
 * AdminCafeManager
 * 카페 관리 전용 어드민 페이지
 * - 카페 등록, 수정, 삭제 기능
 * - 전체 카페 목록 관리
 */
const AdminCafeManager = () => {
  const theme = useTheme();
  const isMobile = useIsMobile('xl');
  const { profile, isLogin, userLoading } = useUser();

  const [cafes, setCafes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [editingCafe, setEditingCafe] = useState(null);
  const [detailCafe, setDetailCafe] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cafeToDelete, setCafeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // 2025-09 이슈 데이터 관련 state
  const [participants, setParticipants] = useState([]);
  const [collections, setCollections] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  // 어드민 권한 확인
  useEffect(() => {
    const verify = async () => {
      try {
        if (userLoading || !isLogin || !profile?.id) return;
        const ok = await checkAdminPermission(profile.id);
        setIsAdmin(!!ok);
        if (!ok) {
          setError('관리자 권한이 필요합니다.');
        }
      } catch (err) {
        setIsAdmin(false);
        setError('권한 확인 중 오류가 발생했습니다.');
      }
    };
    verify();
  }, [profile?.id, isLogin, userLoading]);

  // 카페 목록 구독 (승인 대기 포함)
  useEffect(() => {
    const unsub = subscribeCafeList((list) => {
      const arr = Array.isArray(list) ? list : [];
      setCafes(arr);
      setLoading(false);
    }, { includesPending: true, isAdmin: true }); // 승인 대기 카페도 포함
    return () => {
      try {
        unsub && unsub();
      } catch (_) {}
    };
  }, []);

  // 2025-09 이슈 데이터 구독 (참가자 및 컬렉션 정보)
  useEffect(() => {
    if (!isAdmin) return;

    const issueId = '2025-09';
    const MAG_ROOT = 'magazines';
    const MAG_ID = 'monthlyThreads';

    // 이슈 데이터 구독
    const issueRef = dbService
      .collection(MAG_ROOT)
      .doc(MAG_ID)
      .collection('issues')
      .doc(issueId);

    const unsubIssue = issueRef.onSnapshot(
      async (issueSnap) => {
        if (!issueSnap.exists) {
          setParticipantsLoading(false);
          setCollectionsLoading(false);
          return;
        }

        const issueData = issueSnap.data();

        // 참가자 데이터 로드
        if (issueData?.userIdList && Array.isArray(issueData.userIdList)) {
          try {
            const userPromises = issueData.userIdList.map(userId =>
              dbService.collection('user').doc(userId).get()
            );
            const userSnaps = await Promise.all(userPromises);
            const users = userSnaps
              .filter(snap => snap.exists)
              .map(snap => ({ id: snap.id, ...snap.data() }));
            setParticipants(users);
          } catch (err) {
            console.error('참가자 데이터 로드 실패:', err);
          }
        }
        setParticipantsLoading(false);

        // items 컬렉션에서 컬렉션 정보 로드
        const itemsRef = issueRef.collection('items');
        const itemsSnap = await itemsRef.get();

        if (!itemsSnap.empty) {
          const collectionIds = new Set();
          itemsSnap.docs.forEach(doc => {
            const data = doc.data();
            if (data.collectionId) {
              collectionIds.add(data.collectionId);
            }
          });

          // 컬렉션 정보 로드
          try {
            const collectionPromises = Array.from(collectionIds).map(collectionId =>
              dbService.collection('collection').doc(collectionId).get()
            );
            const collectionSnaps = await Promise.all(collectionPromises);
            const collectionsData = collectionSnaps
              .filter(snap => snap.exists)
              .map(snap => ({
                id: snap.id,
                title: snap.data()?.title || snap.data()?.name || snap.id,
                ...snap.data()
              }));
            setCollections(collectionsData);
          } catch (err) {
            console.error('컬렉션 데이터 로드 실패:', err);
          }
        }
        setCollectionsLoading(false);
      },
      (err) => {
        console.error('이슈 데이터 구독 실패:', err);
        setParticipantsLoading(false);
        setCollectionsLoading(false);
      }
    );

    return () => {
      try {
        unsubIssue && unsubIssue();
      } catch (_) {}
    };
  }, [isAdmin]);

  // 카페 삭제 핸들러
  const handleDeleteClick = (cafe) => {
    setCafeToDelete(cafe);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cafeToDelete) return;

    try {
      setDeleting(true);
      await deleteCafe(cafeToDelete.id);
      setDeleteConfirmOpen(false);
      setCafeToDelete(null);
    } catch (err) {
      console.error('카페 삭제 중 오류:', err);
      alert('카페 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setCafeToDelete(null);
  };


  // 권한이 없거나 로딩 중일 때 처리
  if (userLoading || loading) {
    return (
      <PageContainer>
        <Stack spacing={4} sx={{ py: 4 }}>
          <Skeleton variant="text" sx={{ fontSize: '2rem', width: '40%' }} />
          <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                <Skeleton
                  variant="rectangular"
                  height={200}
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </PageContainer>
    );
  }

  if (error || !isAdmin) {
    return (
      <PageContainer>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center'
        }}>
          <Typography variant="h5" color="error" gutterBottom>
            접근 권한이 없습니다
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error || '관리자만 접근할 수 있는 페이지입니다.'}
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack spacing={4} sx={{ py: 4 }}>
        {/* 헤더 */}
        <Box>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary
            }}
          >
            카페 관리
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            ThreadsCafe 지도에 표시될 카페를 등록하고 관리합니다.
          </Typography>
        </Box>

        {/* 관리 버튼들 */}
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{ alignItems: "flex-start" }}
        >
          <ButtonBasic
            size="md"
            actionType="primary"
            variant="contained"
            onClick={() => {
              setEditingCafe(null);
              setAdminOpen(true);
            }}
          >
            새 카페 등록
          </ButtonBasic>
          <ButtonBasic
            size="md"
            actionType="secondary"
            variant="outlined"
            onClick={() => setListOpen(true)}
          >
            카페 목록 수정
          </ButtonBasic>
        </Stack>

        {/* 통계 정보 */}
        <Box sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Typography variant="h6" gutterBottom>
            현황
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {cafes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                총 카페 수
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="h4" color="success.main" gutterBottom>
                {cafes.filter(c => c.magazineParticipant).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                매거진 참여 카페
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="h4" color="info.main" gutterBottom>
                {cafes.filter(c => c.registrationType === 'user' && c.threadsPostId).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                사용자 등록 카페
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 3 }}>
              <Typography variant="h4" color="secondary.main" gutterBottom>
                {cafes.filter(c => !c.registrationType || !c.threadsPostId).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                레거시 카페
              </Typography>
            </Grid>
          </Grid>
        </Box>


        {/* 전체 카페 목록 */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            모든 카페 목록
          </Typography>
          {cafes.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="body1" color="text.secondary">
                등록된 카페가 없습니다.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                "새 카페 등록" 버튼을 클릭하여 첫 번째 카페를 등록해보세요.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {cafes.map((cafe) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={cafe.id}>
                  <Box sx={{ position: 'relative' }}>
                    <CafeCard
                      cafe={cafe}
                      onMore={() => setDetailCafe(cafe)}
                      showEditButton={true}
                      onEdit={() => {
                        setEditingCafe(cafe);
                        setAdminOpen(true);
                      }}
                      showDeleteButton={true}
                      onDelete={() => handleDeleteClick(cafe)}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* 모달들 */}
        <CafeAdminModal
          opened={adminOpen}
          onClose={() => {
            setAdminOpen(false);
            setEditingCafe(null);
          }}
          initialValue={editingCafe}
          participants={participants}
          collections={collections}
        />

        <CafeAdminListModal
          opened={listOpen}
          onClose={() => setListOpen(false)}
          cafes={cafes}
          onEdit={(cafe) => {
            setEditingCafe(cafe);
            setAdminOpen(true);
            setListOpen(false);
          }}
        />

        <CafeDetailModal
          opened={!!detailCafe}
          onClose={() => setDetailCafe(null)}
          cafe={detailCafe}
        />

        {/* 삭제 확인 다이얼로그 */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            카페 삭제 확인
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              정말로 "{cafeToDelete?.name}" 카페를 삭제하시겠습니까?
              <br />
              삭제된 카페는 복구할 수 없습니다.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              취소
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </PageContainer>
  );
};

export default AdminCafeManager;